import { BrandMark, Button, Card } from "@artisan/ui";
import { CloudOff, Factory, LogIn, PenLine, Plus, Ruler, Share, Smartphone, Sparkles, SquarePlus } from "lucide-react";
import { Link } from "react-router";

import { appBase, webUrl } from "../lib/env";

const FEATURES = [
  {
    icon: Ruler,
    title: "Миллиметрийн нарийвчлалтай хэмжилт",
    text: "Ханын урт, диагональ, нээлхий, инженерийн цэгийг хэмжинэ. Өнцгийг таамаглахгүй, өрөө битүүрэхийг апп өөрөө шалгана.",
  },
  {
    icon: Sparkles,
    title: "AI render-ээр батлуулна",
    text: "Каталогийн барааг өрөөний зураг дээр байрлуулж, харилцагчид бодит харагдацыг өмнө нь дараагаар нь үзүүлнэ.",
  },
  {
    icon: PenLine,
    title: "Гарын үсгээр баталгаажуулна",
    text: "Харилцагч баталсан render болон хэмжилтэд iPad дээрээ шууд гарын үсэг зурна.",
  },
  {
    icon: CloudOff,
    title: "Интернэтгүй ч ажиллана",
    text: "Хэмжилт, зураг төхөөрөмж дээр хадгалагдаж, сүлжээ орж ирэхэд өөрөө үйлдвэрт илгээгдэнэ.",
  },
  {
    icon: Factory,
    title: "Үйлдвэрт шууд очно",
    text: "Хэмжилт нэг баримт болж үйлдвэрийн програмд хүрнэ. Дахин бичих, утсаар тайлбарлах шаардлагагүй.",
  },
];

const STEPS = [
  { icon: Smartphone, title: "Safari-аар нээнэ", text: "iPad дээрээ Safari нээж энэ хаягаар орно." },
  { icon: Share, title: "Хуваалцах товчийг дарна", text: "Safari-ийн хаягийн мөрний хажууд байгаа дөрвөлжин дээр сум бүхий дүрс." },
  { icon: SquarePlus, title: "«Нүүр дэлгэцэнд нэмэх»", text: "Нэрийг нь «Artisan» гэж үлдээгээд «Нэмэх» дарна." },
  { icon: LogIn, title: "Нээгээд нэвтэрнэ", text: "Нүүр дэлгэц дээрх Artisan дүрсээр нээнэ. Бүтэн дэлгэцээр, офлайн ажиллана." },
];

/** What a visitor sees at /field/ before signing in: what the app is, how to put it on an iPad,
 * and the way in. The installed app and an expired session go straight to the login form. */
export function IntroScreen() {
  const signup = webUrl("/signup");
  return (
    <div className="h-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background/90 px-6 py-3 backdrop-blur">
        <BrandMark size={32} labelled={false} />
        <span className="font-display text-lg font-semibold tracking-tight">Artisan</span>
        <span className="hidden text-sm text-muted-foreground sm:inline">· Хээрийн апп</span>
        <span className="flex-1" />
        {signup ? (
          <Button asChild variant="outline">
            <a href={signup} target="_blank" rel="noreferrer">
              <Plus /> Бүртгэл үүсгэх
            </a>
          </Button>
        ) : null}
        <Button asChild>
          <Link to="/login">
            <LogIn /> Нэвтрэх
          </Link>
        </Button>
      </header>

      <main>
        <section className="bg-drafting-grid">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-14 md:grid-cols-[1fr_1.1fr] md:py-20">
            <div>
              <p className="text-overline text-muted-foreground uppercase">iPad апп · борлуулагчид зориулсан</p>
              <h1 className="mt-3 font-display text-4xl leading-[1.1] font-semibold tracking-tight md:text-5xl">Хэмжээд, батлуулаад, үйлдвэрт илгээ.</h1>
              <p className="mt-4 max-w-lg text-lg text-muted-foreground">
                Айлд очиж хана, нээлхий, инженерийн цэгийг хэмжиж, загвараа AI render-ээр батлуулаад, гарын үсэг авч, хэмжээсийг үйлдвэрт нэг дор илгээнэ.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild size="lg">
                  <Link to="/login">
                    <LogIn /> Нэвтрэх
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="#install">iPad дээр суулгах</a>
                </Button>
              </div>
            </div>
            <figure className="overflow-hidden rounded-xl border bg-card shadow-lg">
              <img src={`${appBase}intro-studio.jpg`} alt="AI render студи: өрөөний зураг дээр каталогийн бараа байрлуулсан" className="block w-full" />
            </figure>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 py-14" aria-labelledby="features">
          <h2 id="features" className="font-display text-2xl font-semibold tracking-tight md:text-3xl">
            Нэг аппаар айлын хэмжилтээс үйлдвэр хүртэл
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <Card key={f.title} className="gap-3 p-6">
                <span className="grid size-10 place-items-center rounded-lg bg-primary-soft text-primary">
                  <f.icon className="size-5" aria-hidden />
                </span>
                <h3 className="font-display text-lg font-semibold tracking-tight">{f.title}</h3>
                <p className="text-[15px] text-muted-foreground">{f.text}</p>
              </Card>
            ))}
            <figure className="overflow-hidden rounded-lg border bg-card shadow-xs sm:col-span-2 lg:col-span-1">
              <img src={`${appBase}intro-measure.jpg`} alt="Г хэлбэрийн гал тогооны хэмжилт: ханын урт, диагональ, талбай" className="block size-full object-cover object-top" />
            </figure>
          </div>
        </section>

        <section id="install" className="border-y bg-muted/40" aria-labelledby="install-title">
          <div className="mx-auto max-w-6xl px-6 py-14">
            <h2 id="install-title" className="font-display text-2xl font-semibold tracking-tight md:text-3xl">
              iPad дээрээ апп болгож суулгана
            </h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              App Store-оос татах шаардлагагүй. Нүүр дэлгэцэнд нэмсэн апп бүтэн дэлгэцээр нээгдэж, интернэтгүй үед ч өгөгдлийг найдвартай хадгална.
            </p>
            <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <li key={s.title}>
                  <Card className="h-full gap-3 p-6">
                    <div className="flex items-center gap-3">
                      <span className="grid size-8 place-items-center rounded-full bg-foreground font-mono text-sm font-semibold text-background">{i + 1}</span>
                      <s.icon className="size-5 text-muted-foreground" aria-hidden />
                    </div>
                    <h3 className="font-semibold">{s.title}</h3>
                    <p className="text-sm text-muted-foreground">{s.text}</p>
                  </Card>
                </li>
              ))}
            </ol>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg">
                <Link to="/login">
                  <LogIn /> Нэвтрэх
                </Link>
              </Button>
              {signup ? (
                <p className="text-sm text-muted-foreground">
                  Бүртгэлгүй юу?{" "}
                  <a className="text-primary underline underline-offset-2" href={signup} target="_blank" rel="noreferrer">
                    Байгууллагаа бүртгүүлж, борлуулагчаа урина уу.
                  </a>
                </p>
              ) : null}
            </div>
          </div>
        </section>
      </main>

      <footer className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-6 py-8 text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <BrandMark size={20} labelled={false} /> Artisan
        </span>
        <span>Тавилга, интерьерийн салбарын платформ</span>
      </footer>
    </div>
  );
}
